import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  UserCircle, Key, IdentificationCard, BookOpen, Calculator, DeviceMobile, Handshake, SignOut, CaretDown,
} from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { MemberDocuments } from '../MemberDocuments';
import DisguiseSetup from '../DisguiseSetup';
import TwoFactorSettings from '../TwoFactorSettings';
import PushToggle from '../PushToggle';
import AppLockToggle from '../AppLockToggle';
import InstallGuide from '../InstallGuide';
import SettingRow, { SettingGroup } from '../shell/SettingRow';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** A row that opens its own part of the page in place, rather than a new one. */
const Expandable = ({ icon, label, hint, testid, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <SettingRow
        icon={icon}
        label={label}
        hint={hint}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        trailing={<CaretDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />}
        data-testid={testid}
      />
      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
};

/**
 * The personal half of More, shared by members and staff: Account, Security,
 * Membership and Application, with Sign out set apart. Nothing here is new;
 * every row is a setting that already existed, most of them in the account
 * menu that used to hang off the Support header.
 *
 * `staff` folds Membership into Account: staff reach the Sworn through their
 * Society group instead, and the blueprint names their personal groups
 * "Account & Application".
 *
 * The wording of each setting is its own and was left alone.
 */
const AccountSections = ({ staff = false }) => {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();

  const [displayName, setDisplayName] = useState(user?.display_name || user?.name || '');
  const [name, setName] = useState(user?.name || '');
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [savingPw, setSavingPw] = useState(false);
  const [pwError, setPwError] = useState('');

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await axios.patch(
        `${API_URL}/api/users/me`,
        { name: name.trim(), display_name: displayName.trim() },
        { withCredentials: true }
      );
      await checkAuth(); // refresh the user in context so the UI updates
      toast.success('Profile updated');
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Could not save your profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    if (newPw.length < 12) { setPwError('The new password must be at least 12 characters'); return; }
    if (newPw !== confirmPw) { setPwError('The two new passwords do not match'); return; }
    setSavingPw(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/users/me/password`,
        { current_password: currentPw, new_password: newPw },
        { withCredentials: true }
      );
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      toast.success(data.message || 'Password changed');
    } catch (e2) {
      setPwError(e2.response?.data?.detail || 'Could not change your password');
    } finally {
      setSavingPw(false);
    }
  };

  const membershipRows = (
    <>
      <SettingRow
        icon={IdentificationCard}
        label="Membership card"
        onClick={() => navigate('/membership')}
        data-testid="open-member-card"
      />
      <Expandable icon={BookOpen} label="Society documents" testid="more-documents">
        <MemberDocuments />
      </Expandable>
    </>
  );

  return (
        <>

          <SettingGroup title={staff ? 'Account & Application' : 'Account'} testid="more-account">
            <Expandable icon={UserCircle} label="Your name" hint="Display name and full name" testid="more-profile">
              <form onSubmit={saveProfile} className="space-y-4 pt-1" data-testid="settings-profile">
                <div className="space-y-2">
                  <Label htmlFor="display">Display name</Label>
                  <Input id="display" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="What others see" maxLength={80} data-testid="settings-display-name" />
                  <p className="text-xs text-muted-foreground">This is the name shown on your messages and membership card.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fullname">Full name</Label>
                  <Input id="fullname" value={name} onChange={(e) => setName(e.target.value)}
                    maxLength={80} data-testid="settings-name" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={user?.email || ''} disabled className="opacity-60" />
                  <p className="text-xs text-muted-foreground">Your email is your sign-in and can only be changed by the team.</p>
                </div>
                <Button type="submit" disabled={savingProfile} className="w-full h-11 bg-primary hover:bg-primary/90" data-testid="settings-save-profile">
                  {savingProfile ? 'Saving…' : 'Save changes'}
                </Button>
              </form>
            </Expandable>

            <Expandable icon={Key} label="Change password" testid="more-password">
              <form onSubmit={savePassword} className="space-y-4 pt-1" data-testid="settings-password">
                <div className="space-y-2">
                  <Label htmlFor="cur">Current password</Label>
                  <Input id="cur" type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
                    required autoComplete="current-password" data-testid="settings-current-pw" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new">New password</Label>
                  <Input id="new" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                    required autoComplete="new-password" placeholder="At least 12 characters" data-testid="settings-new-pw" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="conf">Repeat new password</Label>
                  <Input id="conf" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                    required autoComplete="new-password" data-testid="settings-confirm-pw" />
                </div>
                {pwError && <p className="text-sm text-destructive">{pwError}</p>}
                <p className="text-xs text-muted-foreground">
                  Changing your password signs you out on all your other devices. This device stays signed in.
                </p>
                <Button type="submit" disabled={savingPw} className="w-full h-11 bg-primary hover:bg-primary/90" data-testid="settings-save-password">
                  {savingPw ? 'Changing…' : 'Change password'}
                </Button>
              </form>
            </Expandable>
            {staff && membershipRows}
            {staff && (
              <Expandable icon={DeviceMobile} label="Install Arcanum" hint="Add it to this device's home screen" testid="more-install">
                <InstallGuide compact />
              </Expandable>
            )}
          </SettingGroup>

          <SettingGroup title="Security" testid="more-security">
            <TwoFactorSettings />
            <PushToggle />
            <AppLockToggle />
            <Expandable icon={Calculator} label="Disguise" hint="How Arcanum looks on this device" testid="more-disguise">
              <DisguiseSetup />
            </Expandable>
          </SettingGroup>

          {!staff && (
            <SettingGroup title="Membership" testid="more-membership">
              {membershipRows}
              {/* Only for those in it -- absent, not greyed out, for everyone
                  else (docs/sworn_design.md §3.1). */}
              {user?.is_sworn === true && (
                <SettingRow icon={Handshake} label="The Sworn" onClick={() => navigate('/sworn')} data-testid="more-sworn" />
              )}
            </SettingGroup>
          )}

          {!staff && (
            <SettingGroup title="Application" testid="more-application">
              <Expandable icon={DeviceMobile} label="Install Arcanum" hint="Add it to this device's home screen" testid="more-install">
                <InstallGuide compact />
              </Expandable>
            </SettingGroup>
          )}

          {/* Apart from the rest, and marked, but no bigger than anything else:
              leaving should be easy to find and hard to hit by accident. */}
          <div className="overflow-hidden rounded-xl border border-destructive/30 bg-card/85 backdrop-blur-sm">
            <SettingRow
              icon={SignOut}
              tone="destructive"
              label="Sign out"
              trailing={null}
              onClick={() => navigate('/farewell')}
              data-testid="logout-button"
            />
          </div>
        </>
  );
};

export default AccountSections;
